import { motion, AnimatePresence } from 'framer-motion';
import { Grid, List, Search, X } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { productos } from '../data/db/Products';
import MobileCartButton from '../components/MobileCartIcon';
import '../index.css';
import ProductCount from '../components/TotalProducts';
import {handleBuy} from '../functions/handleBuy'

const ProductosPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState('');
  const [view, setView] = useState('grid');
  const [sort, setSort] = useState('default');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [itemsPerPage] = useState(6);

  // Obtener la página actual de la URL o usar 1 por defecto
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  // Actualizar la URL cuando cambia la página
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setSearchParams({ page: newPage.toString() });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Optimización: Calcular productos ordenados y filtrados solo cuando sea necesario
  const sortedProductos = useMemo(() => {
    return [...productos].sort((a, b) => {
      if (sort === 'price-high-low') return b.precio - a.precio;
      if (sort === 'price-low-high') return a.precio - b.precio;
      return 0;
    });
  }, [sort]);

  const filteredProductos = useMemo(() => {
    return sortedProductos.filter(producto =>
      producto.titulo.toLowerCase().includes(filter.toLowerCase())
    );
  }, [filter, sortedProductos]);

  // Calcular productos paginados
  const paginatedProductos = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredProductos.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, filteredProductos, itemsPerPage]);

  const totalPages = Math.ceil(filteredProductos.length / itemsPerPage);

  // Resetear a página 1 cuando cambian los filtros
  useEffect(() => {
    setSearchParams({ page: '1' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, sort]);

  

  const handleProductClick = (producto: typeof productos[0]) => {
    const cantidadInput = document.getElementById(`cantidad-${producto.id}`) as HTMLInputElement;
    const cantidad = cantidadInput ? cantidadInput.value : '1';
  
    const productoSeleccionado = {
      titulo: producto.titulo,
      categoria: producto.categoria,
      precio: producto.precio,
      cantidad
    };
  
    localStorage.setItem('productoSeleccionado', JSON.stringify(productoSeleccionado));
  
    // Guardar estado actual en localStorage
    localStorage.setItem('prevPageState', JSON.stringify({
      filter,
      sort,
      view,
      currentPage
    }));
  
    navigate(`/producto/${producto.id}?fromPage=${currentPage}`);
  };
  
  useEffect(() => {
    const productoSeleccionado = JSON.parse(localStorage.getItem('productoSeleccionado') || '{}');
    if (productoSeleccionado && productoSeleccionado.id) {
      const cantidadInput = document.getElementById(`cantidad-${productoSeleccionado.id}`) as HTMLInputElement;
      if (cantidadInput) {
        cantidadInput.value = productoSeleccionado.cantidad;
      }
    }
  }, []);

  return (
    <div className="min-h-screen overflow-hidden bg-main-bg p-8">
      <MobileCartButton />
      <ProductCount/>

      {/* Sidebar para móviles */}
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'tween' }}
            className="fixed inset-y-0 left-0 w-64 bg-white shadow-2xl z-50 p-6 md:hidden"
            aria-hidden={!isSidebarOpen}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Buscar</h2>
              <button type='button'
                onClick={() => setIsSidebarOpen(false)}
                className="p-2 rounded-full hover:bg-gray-100"
                aria-label="Cerrar menú de búsqueda"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <input
              type="text"
              placeholder="Buscar productos..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full p-3 rounded-lg border-2 border-gray-200 focus:outline-none focus:border-primary"
              aria-label="Buscar productos"
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Encabezado */}
      <div
        
        className="mb-12 text-center"
      >
        <h1 className="text-2xl md:text-4xl font-poppins font-bold text-dark mb-4">
          Nuestros Productos Médicos
        </h1>
        <p className="text-dark font-montserrat max-w-2xl mx-auto">
          Tecnología de vanguardia para profesionales de la salud
        </p>
      </div>

      {/* Filtros */}
      <div className="mb-8 max-w-7xl mx-auto flex justify-between items-center gap-4">
        <button
          type="button"
          title="Buscar"
          onClick={() => setIsSidebarOpen(true)}
          className="md:hidden p-3 rounded-lg bg-primary text-white hover:bg-primary-dark"
          aria-label="Abrir barra lateral de búsqueda"
        >
          <Search className="w-5 h-5" />
        </button>

        <input
          type="text"
          placeholder="Buscar productos..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="hidden md:block w-[30rem] p-3 rounded-lg border-2 border-gray-200 focus:outline-none focus:border-primary"
          aria-label="Buscar productos"
        />

        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="p-3 rounded-lg border-2 border-gray-200 focus:outline-none focus:border-primary"
          aria-label="Ordenar productos"
        >
          <option value="default">Ordenar por</option>
          <option value="price-low-high">Precio: Bajo a Alto</option>
          <option value="price-high-low">Precio: Alto a Bajo</option>
        </select>

        <div className="flex gap-2">
          <button type='button'
            onClick={() => setView('grid')}
            className={`p-3 rounded-lg ${view === 'grid' ? 'bg-primary text-white' : 'bg-gray-100'}`}
            aria-label="Ver en vista de cuadrícula"
          >
            <Grid className="w-5 h-5" />
          </button>
          <button type='button'
            onClick={() => setView('list')}
            className={`p-3 rounded-lg ${view === 'list' ? 'bg-primary text-white' : 'bg-gray-100'}`}
            aria-label="Ver en vista de lista"
          >
            <List className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Listado de Productos */}
      <div className={`grid ${view === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'} gap-8 max-w-7xl mx-auto`}>
        {paginatedProductos.map((producto) => (
          <div
            key={producto.id}
           
            className={`bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 ${view === 'list' ? 'p-4 flex flex-col' : 'p-6'}`}
          >
            <div onClick={() => handleProductClick(producto)} className="cursor-pointer mb-4" aria-label={`Ver detalles del ${producto.titulo}`}>
              <img src={producto.imagenes[0]} alt={producto.titulo} className="w-full h-48 object-contain rounded-xl mb-4" />
              <h3 className="text-lg font-semibold text-dark">{producto.titulo}</h3>
              <p className="text-sm text-dark">{producto.descripcion.split(' ').slice(0, 16).join(' ')}...</p>
              </div>
            <div className="flex justify-between items-center">
              <span className="text-xl font-semibold text-primary">${producto.precio}</span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  id={`cantidad-${producto.id}`}
                  defaultValue="1"
                  min="1"
                  max={producto.stock}
                  className="w-16 p-2 border-2 border-gray-300 rounded-md"
                  aria-label="Cantidad"
                />
                <button type='button'
                  onClick={() => handleBuy(producto)}
                  className="bg-primary text-white px-4 py-2 rounded-lg"
                  aria-label="Agregar al carrito"
                >
                  Agregar
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Paginación */}
      {filteredProductos.length > 0 && (
        <div
          
          className="flex overflow-x-auto scrollbar-hide whitespace-nowrap mt-8 gap-2 pb-2 lg:justify-center lg:overflow-visible"
        >
          <button type='button'
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-lg flex-shrink-0 ${
              currentPage === 1 
                ? 'bg-gray-200 cursor-not-allowed' 
                : 'bg-primary text-white hover:bg-primary-dark'
            }`}
            aria-label="Página anterior"
          >
            Anterior
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button type='button'
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-4 py-2 rounded-lg flex-shrink-0 ${
                currentPage === page
                  ? 'bg-primary text-white'
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
              aria-label={`Ir a página ${page}`}
            >
              {page}
            </button>
          ))}

          <button type='button'
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded-lg flex-shrink-0 ${
              currentPage === totalPages
                ? 'bg-gray-200 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-primary-dark'
            }`}
            aria-label="Página siguiente"
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductosPage;